import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/design-system/card'
import { Button } from '@/design-system/button'
import { Badge } from '@/design-system/badge'
import {
  ArrowRight, ArrowDown, Brain, Users, Bot, Video, Shuffle,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Solution components data                                           */
/* ------------------------------------------------------------------ */

const prospectSide = [
  {
    number: '1',
    title: 'Prospect Persona Research',
    icon: Users,
    color: 'border-t-blue-500',
    bullets: [
      'Quantitative survey (250\u2013300 responses)',
      'Semi-structured interviews (20\u201325)',
      'Converted leads track',
      'Non-converted leads track',
    ],
  },
  {
    number: '5',
    title: 'AI Chatbot (Website Form)',
    icon: Bot,
    color: 'border-t-blue-400',
    bullets: [
      'Conversational flow on NN\u2019s web form',
      'Adapts questions per lead\u2019s answers',
      'Sentiment & engagement scoring',
      'Real-time lead profiling',
    ],
  },
]

const consultantSide = [
  {
    number: '2',
    title: 'Consultant Persona Research',
    icon: Brain,
    color: 'border-t-amber-500',
    bullets: [
      'Quantitative survey (282 consultants, 14 dimensions)',
      'Semi-structured interviews (20\u201325)',
    ],
  },
  {
    number: '4',
    title: 'AI Voice Agent (TA Profiling)',
    icon: Video,
    color: 'border-t-amber-400',
    bullets: [
      'AI avatar conducts structured interview',
      'Video recorded & auto-analyzed',
      'Behavioral attributes auto-extracted',
      'Auto-classifies into 1 of 12 microprofiles',
    ],
  },
]

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ExecutivePage() {
  return (
    <div className="space-y-16 pb-16">

      {/* ============================================================ */}
      {/*  HERO                                                        */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden border-b bg-gradient-to-br from-background via-background to-[#F17922]/5 px-6 py-20">
        <div className="mx-auto max-w-7xl">
          {/* Branding */}
          <div className="flex items-center gap-4 mb-8">
            <img src="/nn-logo.svg" alt="NN" className="h-14" />
            <div>
              <Badge className="bg-[#F17922] text-white">Executive Project Synthesis</Badge>
              <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest">
                Confidential &mdash; Prepared for NN Leadership &middot; February 2026
              </p>
            </div>
          </div>

          {/* Headline */}
          <h1 className="max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Behavioral Intelligence for{' '}
            <span className="text-[#F17922]">Lead Conversion</span>
          </h1>

          {/* Subtitle */}
          <p className="mt-6 max-w-3xl text-lg text-gray-600 leading-relaxed">
            Understanding both sides of the sale &mdash; what motivates customers and how consultants
            actually sell &mdash; powered by AI to build a smarter matching system for NN.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex gap-4">
            <Button asChild size="lg" className="bg-[#F17922] hover:bg-[#D96810]">
              <Link to="/research">
                View Research <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/profiles">Explore Profiles</Link>
            </Button>
          </div>
        </div>
        <div className="pointer-events-none absolute -right-32 -top-32 size-96 bg-[#F17922]/5 blur-3xl" />
      </section>

      {/* ============================================================ */}
      {/*  THE OPPORTUNITY                                              */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-4">
            The Opportunity
          </h2>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              NN generates thousands of leads every month. A prospect fills out a form.
              A consultant calls. And then &mdash; a significant percentage of those prospects
              never convert.
            </p>
            <p>
              The common assumption is that conversion depends on lead quality or product pricing.
              Our research reveals an additional, powerful lever:{' '}
              <span className="font-semibold text-gray-900">
                the behavioral fit between the consultant and the prospect.
              </span>
            </p>
            <p>
              Understanding this fit &mdash; and acting on it &mdash; means NN can improve
              conversion rates without changing the product, the price, or the lead volume.
              The same leads, routed to the right consultant, produce different results.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  HOW WE DESIGNED THE SOLUTION                                */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="mb-8 max-w-3xl">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            How We Designed the Solution
          </h2>
          <p className="mt-2 text-muted-foreground">
            The problem has two sides: we don&rsquo;t truly know the prospect, and we don&rsquo;t
            truly know the consultant. A matching system requires deep intelligence on both.
          </p>
          <p className="mt-2 text-sm text-gray-600">
            We designed five components that work as a single system:
          </p>
        </div>

        {/* Architecture diagram */}
        <div className="space-y-6">

          {/* Column headers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-widest text-blue-600">
                Understand the Prospect
              </span>
            </div>
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-600">
                Understand the Consultant
              </span>
            </div>
          </div>

          {/* Row 1: Research */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[prospectSide[0], consultantSide[0]].map((comp) => (
              <Card key={comp.title} className={`border-t-4 ${comp.color}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <comp.icon className="w-4 h-4 text-gray-500" />
                    <CardTitle className="text-base">{comp.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {comp.bullets.map((b, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                        <span className="text-gray-400 mt-0.5">&bull;</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Arrow down */}
          <div className="flex justify-center">
            <ArrowDown className="w-5 h-5 text-gray-400" />
          </div>

          {/* Row 2: AI Tools */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[prospectSide[1], consultantSide[1]].map((comp) => (
              <Card key={comp.title} className={`border-t-4 ${comp.color}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <comp.icon className="w-4 h-4 text-gray-500" />
                    <CardTitle className="text-base">{comp.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {comp.bullets.map((b, i) => (
                      <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                        <span className="text-gray-400 mt-0.5">&bull;</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Connector */}
          <div className="flex justify-center">
            <div className="flex flex-col items-center gap-2">
              <ArrowDown className="w-5 h-5 text-gray-400" />
              <div className="border border-dashed border-gray-300 bg-gray-50 px-6 py-3 text-center max-w-lg">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Solid foundation of deep behavioral data and statistical methods on both sides enables:
                </p>
              </div>
              <ArrowDown className="w-5 h-5 text-gray-400" />
            </div>
          </div>

          {/* Row 3: Matching Algorithm — AI-inspired centered card */}
          <div className="relative rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-blue-50 p-8 text-center overflow-hidden">
            {/* Decorative AI circuit lines */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #059669 1px, transparent 0)', backgroundSize: '24px 24px' }} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-1.5 mb-4">
                <Shuffle className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">AI-Powered</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Matching Algorithm</h3>
              <p className="text-sm text-gray-600 max-w-xl mx-auto mb-6">
                Lead Profile &times; Consultant Profile = Compatibility Score
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  'Rule-based logic first',
                  'ML-enhanced over time',
                  'A/B tested vs. current method',
                  'CRM & analytics integration',
                  'Continuously improving',
                ].map((tag, i) => (
                  <span key={i} className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs text-gray-700">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}
